import { useEffect, useState } from 'react';
import { PageHeader } from '../../../components/layout/Header';
import { Button, Select } from '../../../components/ui';
import SearchInput from '../../../components/ui/SearchInput';
import Spinner from '../../../components/ui/Spinner';
import { useAdminPartners } from '../hooks/useAdminPartners';
import type { AdminPartnerRow } from '../../../domain/partners/adminTypes';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'pending', label: 'Pendentes' },
  { value: 'active', label: 'Ativos' },
  { value: 'rejected', label: 'Rejeitados' },
  { value: 'suspended', label: 'Suspensos' },
];

function statusLabel(status: string): string {
  const found = STATUS_OPTIONS.find((o) => o.value === status);
  return found?.label || status;
}

function PartnerActions({
  row,
  onApprove,
  onReject,
  onSuspend,
  isSaving,
}: {
  row: AdminPartnerRow;
  onApprove: () => void;
  onReject: () => void;
  onSuspend: () => void;
  isSaving: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {row.status === 'pending' && (
        <>
          <Button type="button" size="sm" onClick={onApprove} disabled={isSaving}>Aprovar</Button>
          <Button type="button" size="sm" variant="outline" onClick={onReject} disabled={isSaving}>Rejeitar</Button>
        </>
      )}
      {row.status === 'active' && (
        <Button type="button" size="sm" variant="outline" onClick={onSuspend} disabled={isSaving}>Suspender</Button>
      )}
      {row.status === 'suspended' && (
        <Button type="button" size="sm" onClick={onApprove} disabled={isSaving}>Reativar</Button>
      )}
    </div>
  );
}

export function AdminPartnersPage() {
  const {
    data,
    isLoading,
    isSaving,
    error,
    updatePartner,
    runPromoteCommissions,
    page,
    setPage,
    q,
    setQ,
    status,
    setStatus,
    pageSize,
  } = useAdminPartners();

  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, setQ, setPage]);

  const total = data?.total ?? 0;
  const items = data?.items ?? [];

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Parceiros"
        subtitle="Programa de indicação para agências"
        hideBackButton
        action={
          <Button type="button" variant="outline" onClick={() => void runPromoteCommissions()} disabled={isSaving}>
            Promover comissões
          </Button>
        }
      />

      <div className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:p-6">
        <div className="w-full md:max-w-xs">
          <SearchInput value={searchInput} onChange={setSearchInput} placeholder="Buscar agência ou código" />
        </div>
        <Select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          options={STATUS_OPTIONS}
        />
      </div>

      {error && <p className="px-4 text-sm text-danger md:px-6">{error}</p>}

      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : (
        <div className="overflow-x-auto px-4 pb-6 md:px-6">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-default text-left text-secondary">
                <th className="py-2 pr-3">Agência</th>
                <th className="py-2 pr-3">Código</th>
                <th className="py-2 pr-3">Email</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Comissão</th>
                <th className="py-2 pr-3">Indicações</th>
                <th className="py-2 pr-3">Stripe</th>
                <th className="py-2 pr-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-b border-default/60">
                  <td className="py-2 pr-3">{row.companyName}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{row.code}</td>
                  <td className="py-2 pr-3">{row.email || '—'}</td>
                  <td className="py-2 pr-3">{statusLabel(row.status)}</td>
                  <td className="py-2 pr-3">{(row.commissionRate * 100).toFixed(0)}%</td>
                  <td className="py-2 pr-3">{row.referralCount}</td>
                  <td className="py-2 pr-3">{row.connectOnboardingComplete ? 'OK' : 'Pendente'}</td>
                  <td className="py-2 pr-3">
                    <PartnerActions
                      row={row}
                      isSaving={isSaving}
                      onApprove={() => void updatePartner(row.id, { status: 'active' })}
                      onReject={() => void updatePartner(row.id, { status: 'rejected' })}
                      onSuspend={() => void updatePartner(row.id, { status: 'suspended' })}
                    />
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr>
                  <td colSpan={8} className="py-6 text-secondary">Nenhum parceiro encontrado.</td>
                </tr>
              )}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-secondary">
            Página {page} · {total} parceiro(s)
          </p>
        </div>
      )}
    </div>
  );
}
